import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { GeneralService } from '../general.service';
import Swal from 'sweetalert2';

declare var SockJS: any;
declare var Stomp: any;

export interface WebSocketMessage {
  type: string;
  payload: any;
  destination?: string;
}

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private client: any;
  private messages$ = new Subject<WebSocketMessage>();
  private connected$ = new BehaviorSubject<boolean>(false);
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private isConnecting = false;

  // 🧠 Registro de suscripciones dinámicas para re-suscribir tras reconexión
  private dynamicSubscriptions = new Map<string, string>(); // topic -> type

  // 🚨 Control de alertas de servidor
  private serverDownAlert: any = null;
  private isServerDown = false;

  // 🔇 Contador para logs silenciosos
  private silentMessageCount = 0;

  constructor(private auth: GeneralService) {
    // Auto-conectar si hay token
    if (this.auth.getToken()) {
      this.connect();
    } else {
    }
  }

  /**
   * Conectar WebSocket con logs mejorados
   */
  connect(): void {
    if (this.client?.connected || !this.auth.getToken() || this.isConnecting) {
      if (!this.auth.getToken()) {
      }
      return;
    }

    this.isConnecting = true;

    const wsUrl = `${environment.url}ws`;
    const sock = new SockJS(wsUrl);
    this.client = Stomp.over(sock);

    // Desactivar logs de debug
    this.client.debug = () => {};

    this.client.connect(
      {},
      () => {
        this.isConnecting = false;
        this.connected$.next(true);
        this.reconnectAttempts = 0;
        this.hideServerDownAlert();
        this.subscribeToTopics();
        // Re-suscribir a todos los tópicos dinámicos registrados
        this.resubscribeDynamicTopics();
      },
      (error: any) => {
        console.error('❌ ERROR CONECTANDO WEBSOCKET:', error);
        console.error('🔍 Tipo de error:', typeof error);
        console.error(
          '🔍 Error stringificado:',
          JSON.stringify(error, null, 2)
        );

        this.isConnecting = false;

        // Solo mostrar alerta si realmente es un problema del servidor
        if (this.isServerDownError(error)) {
        } else {
        }

        this.handleReconnect();
      }
    );
  }

  /**
   * Suscribirse a tópicos STOMP
   */
  private subscribeToTopics(): void {
    // 📢 Topics que SÍ deben mostrar logs (importantes para debugging)
    const verboseTopics = [
      // Topics generales
      '/topic/notifications', // Notificaciones generales
      '/topic/notifications/deleted', // Notificaciones eliminadas
      '/topic/notifications/read', // Notificaciones marcadas como leídas
      '/topic/notifications/broadcast', // Notificaciones broadcast para todos
      '/topic/notifications/broadcast/deleted', // Eliminación de notificaciones broadcast
      '/topic/notifications/broadcast/read', // Notificaciones broadcast marcadas como leídas
      '/topic/notifications/admin', // Notificaciones de administrador
      '/topic/notifications/admin/deleted', // Eliminación de notificaciones de admin
      '/topic/notifications/admin/read', // Notificaciones de admin marcadas como leídas
      '/topic/faqs', // FAQs

      // Topics de anuncios
      '/topic/anuncios', // Lista general de anuncios
      '/topic/anuncios/recientes', // Anuncios recientes
      '/topic/anuncios/new', // Nuevos anuncios
      '/topic/anuncios/update', // Actualizaciones de anuncios
      '/topic/anuncios/delete', // Eliminaciones de anuncios
      '/topic/anuncios/recientes/delete', // Eliminaciones de anuncios recientes
      '/topic/anuncios/activos', // Anuncios activos
      '/topic/anuncios/todos', // Todos los anuncios
    ];

    // Combinar todos los topics
    const allTopics = [...verboseTopics];

    allTopics.forEach((topic) => {
      this.client.subscribe(topic, (message: any) => {
        try {
          const payload = JSON.parse(message.body);
          const type = topic.split('/').pop()?.toUpperCase() || 'UNKNOWN';

          this.messages$.next({
            type,
            payload,
            destination: topic,
          });

          // 🔇 Silenciar notificaciones masivas y de conexión/desconexión individual
          const isUserConnectionNotification =
            topic === '/topic/notifications' &&
            (payload.type === 'USER_CONNECTED' ||
              payload.type === 'USER_DISCONNECTED');

          const isMassiveStatusUpdate =
            topic === '/topic/notifications' &&
            payload.type === 'USERS_STATUS_UPDATED';

          // 🔇 NUEVO: Silenciar eventos de lectura de otros usuarios
          const isOtherUserReadEvent = this.isOtherUserReadEvent(
            topic,
            payload
          );

          // 🔇 NUEVO: Silenciar notificaciones broadcast de lectura de otros usuarios
          const isOtherUserBroadcastRead = this.isOtherUserBroadcastRead(
            topic,
            payload
          );

          // 🔇 NUEVO: Silenciar eventos de notificaciones con notificationId y userId de otros usuarios
          const isOtherUserNotificationEvent =
            this.isOtherUserNotificationEvent(topic, payload);

          if (
            !isUserConnectionNotification &&
            !isMassiveStatusUpdate &&
            !isOtherUserReadEvent &&
            !isOtherUserBroadcastRead &&
            !isOtherUserNotificationEvent
          ) {
          } else {
            // Para topics silenciosos, solo mostrar un log muy reducido cada 50 mensajes
            this.logSilentMessage(topic, payload);
          }
        } catch (error) {
          console.error('❌ Error parsing STOMP message:', error);
        }
      });
    });
  }

  /**
   * 🔇 NUEVO: Detectar si es un evento de lectura de otro usuario
   */
  private isOtherUserReadEvent(topic: string, payload: any): boolean {
    // Filtrar todos los eventos de lectura que no sean del usuario actual
    if (!topic.includes('/read') || !payload?.userId) {
      return false;
    }

    const currentUserId = this.auth.getUserId();
    const isOtherUser = payload.userId !== currentUserId;

    // Log silencioso para debugging (solo cada 500 eventos)
    if (isOtherUser && this.silentMessageCount % 500 === 0) {
    }

    return isOtherUser;
  }

  /**
   * 🔇 NUEVO: Detectar si es un evento broadcast de lectura de otro usuario
   */
  private isOtherUserBroadcastRead(topic: string, payload: any): boolean {
    // Filtrar eventos broadcast de lectura de otros usuarios
    if (
      !topic.includes('/broadcast') ||
      payload?.type !== 'READ' ||
      !payload?.userId
    ) {
      return false;
    }

    const currentUserId = this.auth.getUserId();
    const isOtherUser = payload.userId !== currentUserId;

    // También filtrar eventos de admin read de otros usuarios
    const isAdminReadFromOtherUser =
      topic.includes('/admin/read') && isOtherUser;

    return isOtherUser || isAdminReadFromOtherUser;
  }

  /**
   * 🔇 NUEVO: Detectar eventos de notificaciones de otros usuarios
   */
  private isOtherUserNotificationEvent(topic: string, payload: any): boolean {
    // Filtrar eventos que tengan notificationId y userId de otros usuarios
    if (!payload?.notificationId || !payload?.userId) {
      return false;
    }

    const currentUserId = this.auth.getUserId();
    const isOtherUser = payload.userId !== currentUserId;

    // Filtrar específicamente eventos de lectura masiva de otros usuarios
    const isMassiveReadEvent =
      topic.includes('/notifications') &&
      (topic.includes('/read') || topic.includes('/broadcast')) &&
      isOtherUser;

    return isMassiveReadEvent;
  }

  /**
   * Log reducido para mensajes silenciosos (cada 50 mensajes)
   */
  private logSilentMessage(topic: string, payload: any): void {
    this.silentMessageCount++;

    // 🔇 Eventos de otros usuarios - log muy reducido
    const isOtherUserEvent =
      this.isOtherUserReadEvent(topic, payload) ||
      this.isOtherUserBroadcastRead(topic, payload) ||
      this.isOtherUserNotificationEvent(topic, payload);

    if (isOtherUserEvent) {
      // Log extremadamente reducido para eventos de otros usuarios (cada 1000 eventos)
      if (this.silentMessageCount % 1000 === 0) {
      }
    } else {
      // Log cada 50 mensajes para otros eventos silenciosos (conexiones, etc.)
      if (this.silentMessageCount % 50 === 0) {
      }
    }
  }

  /**
   * 🔧 Verificar el estado del servidor con health check
   */
  private async checkServerHealth(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${environment.url}health`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${this.auth.getToken()}`,
        },
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('🏥 Health check falló:', error);
      return false;
    }
  }

  /**
   * 🔧 Detección mejorada de servidor realmente caído
   */
  private isServerDownError(error: any): boolean {
    const errorMessage = error?.toString?.() || '';

    // Solo considerar como servidor caído si:
    // 1. Es específicamente un error 502/503/504 (errores de gateway/servidor)
    const isGatewayError =
      errorMessage.includes('502') ||
      errorMessage.includes('503') ||
      errorMessage.includes('504') ||
      errorMessage.includes('Bad Gateway') ||
      errorMessage.includes('Service Unavailable') ||
      errorMessage.includes('Gateway Timeout');

    // 2. Y además hay múltiples reintentos consecutivos (no solo 1)
    const hasMultipleFailures = this.reconnectAttempts >= 3; // Cambiar de 1 a 3

    // 3. O si específicamente dice que la conexión se perdió por reinicio
    const isServerRestart =
      errorMessage.includes('server restart') ||
      errorMessage.includes('server shutdown') ||
      errorMessage.includes('connection reset');

    return (isGatewayError && hasMultipleFailures) || isServerRestart;
  }

  /**
   * 🔧 Manejo mejorado de reconexión con health check
   */
  private async handleReconnect(): Promise<void> {
    this.connected$.next(false);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      // Verificar si realmente el servidor está caído antes de mostrar alerta
      if (this.reconnectAttempts >= 3) {
        const isServerHealthy = await this.checkServerHealth();

        if (!isServerHealthy) {
          // Solo ahora mostrar la alerta de servidor caído
          this.showServerDownAlert();
        } else {
        }
      }

      const delay = Math.min(30000, 5000 * this.reconnectAttempts);

      setTimeout(() => {
        if (this.auth.getToken()) {
          this.connect();
        }
      }, delay);
    } else {
      console.error('❌ Máximo de reintentos alcanzado');
      // Aquí sí mostrar alerta de servidor caído definitivamente
      this.showServerDownAlert();
    }
  }

  /**
   * 🔧 Mejorar el mensaje de alerta para ser más preciso
   */
  private showServerDownAlert(): void {
    if (this.serverDownAlert || this.isServerDown) {
      return;
    }

    this.isServerDown = true;
    this.serverDownAlert = Swal.fire({
      title: '⚠️ Problemas de Conexión',
      html: `
        <div style="text-align: center;">
          <div class="spinner-border text-warning mb-3" role="status" style="width: 3rem; height: 3rem;">
            <span class="sr-only">Cargando...</span>
          </div>
          <p style="font-size: 16px; margin: 15px 0;">
            No se puede conectar al servidor...
          </p>
          <p style="color: #6c757d; font-size: 14px;">
            Posibles causas:
          </p>
          <ul style="color: #6c757d; font-size: 12px; text-align: left; display: inline-block;">
            <li>Servidor temporalmente ocupado</li>
            <li>Problemas de red</li>
            <li>Mantenimiento del sistema</li>
          </ul>
          <p style="color: #6c757d; font-size: 14px;">
            Reconectando cada ${
              Math.min(30000, 5000 * (this.reconnectAttempts + 1)) / 1000
            }s
          </p>
          <p style="color: #6c757d; font-size: 12px;">
            Intento ${this.reconnectAttempts}/${this.maxReconnectAttempts}
          </p>
        </div>
      `,
      icon: 'warning',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      confirmButtonText: '🔄 Reintentar Ahora',
      backdrop: 'rgba(0,0,0,0.8)',
      customClass: {
        popup: 'swal2-popup-server-down',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        // Usuario quiere reintentar manualmente
        this.reconnectAttempts = 0;
        this.hideServerDownAlert();
        this.connect();
      }
    });
  }

  /**
   * ✅ Ocultar alerta de servidor caído
   */
  private hideServerDownAlert(): void {
    if (this.serverDownAlert && this.isServerDown) {
      // Mostrar mensaje de éxito por 3 segundos antes de cerrar
      Swal.fire({
        title: '✅ Servidor Conectado',
        text: 'Conexión restablecida exitosamente',
        icon: 'success',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });

      // Cerrar la alerta principal
      this.serverDownAlert.close();
      this.serverDownAlert = null;
      this.isServerDown = false;
    }
  }

  /**
   * Desconectar WebSocket
   */
  disconnect(): void {
    if (this.client?.connected) {
      this.client.disconnect();
      this.connected$.next(false);
    }
  }

  /**
   * Obtener estado de conexión
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  /**
   * Obtener todos los mensajes
   */
  getMessages(): Observable<WebSocketMessage> {
    return this.messages$.asObservable();
  }

  /**
   * Obtener mensajes por tipo
   */
  getMessagesByType(type: string): Observable<any> {
    return new Observable((observer) => {
      this.messages$.subscribe((message) => {
        if (message.type === type) {
          observer.next(message.payload);
        }
      });
    });
  }

  /**
   * Obtener mensajes por destino/topic específico
   */
  getMessagesByDestination(destination: string): Observable<any> {
    return new Observable((observer) => {
      this.messages$.subscribe((message) => {
        if (message.destination === destination) {
          observer.next(message.payload);
        }
      });
    });
  }

  /**
   * Suscribirse dinámicamente a un topic específico
   * Espera automáticamente a que el WebSocket esté conectado
   */
  subscribeToDynamicTopic(topic: string, type: string = 'DYNAMIC'): void {
    if (!this.client?.connected) {
      // En lugar de mostrar warning, esperar a que se conecte
      this.getConnectionStatus()
        .pipe(
          filter((connected) => connected === true),
          take(1),
          timeout(10000) // Timeout de 10 segundos
        )
        .subscribe({
          next: () => {
            this.performSubscription(topic, type);
          },
          error: () => {
            console.warn(
              '⚠️ Timeout esperando conexión WebSocket para topic:',
              topic
            );
          },
        });
      return;
    }

    this.performSubscription(topic, type);
  }

  /**
   * Realizar la suscripción real al topic
   */
  private performSubscription(topic: string, type: string): void {
    try {
      // Guardar/actualizar el registro de suscripciones para re-suscribir luego
      this.dynamicSubscriptions.set(topic, type);
      this.client.subscribe(topic, (message: any) => {
        try {
          const payload = JSON.parse(message.body);
          const messageType = topic.split('/').pop()?.toUpperCase() || type;

          this.messages$.next({
            type: messageType,
            payload,
            destination: topic,
          });

          // 🔇 Aplicar la misma lógica de logs silenciosos para suscripciones dinámicas
          const isSilent = topic.includes('status');

          if (!isSilent) {
          } else {
            this.logSilentMessage(topic, payload);
          }

          // Log específico para notificaciones de usuario
          if (
            topic.includes('/user/') &&
            topic.includes('/queue/notifications')
          ) {
          }
        } catch (error) {
          console.error('❌ Error parsing dynamic STOMP message:', error);
        }
      });
    } catch (error) {
      console.error(
        `❌ performSubscription - Error suscribiéndose a ${topic}:`,
        error
      );
    }
  }

  /**
   * Re-suscribir todos los tópicos dinámicos tras reconexión
   */
  private resubscribeDynamicTopics(): void {
    if (!this.client?.connected || this.dynamicSubscriptions.size === 0) {
      return;
    }
    this.dynamicSubscriptions.forEach((type, topic) => {
      try {
        this.performSubscription(topic, type);
      } catch (error) {
        console.error(`❌ Error re-suscribiendo a ${topic}:`, error);
      }
    });
  }

  /**
   * Suscribirse a notificaciones específicas de un usuario
   */
  subscribeToUserNotifications(recipientId: number): void {
    const topic = `/topic/notifications/${recipientId}`;
    this.subscribeToDynamicTopic(topic, 'USER_NOTIFICATION');
  }

  /**
   * Suscribirse a notificaciones broadcast
   */
  subscribeToBroadcastNotifications(): void {
    const topic = '/topic/notifications/broadcast';
    this.subscribeToDynamicTopic(topic, 'BROADCAST_NOTIFICATION');
  }

  /**
   * Suscribirse a notificaciones de administrador
   */
  subscribeToAdminNotifications(): void {
    const topic = '/topic/notifications/admin';
    this.subscribeToDynamicTopic(topic, 'ADMIN_NOTIFICATION');
  }

  /**
   * ⚠️ Suscribirse a tópicos de gestión de usuarios
   * Solo para uso en páginas como user-list que necesitan monitorear operaciones de usuarios
   */
  subscribeToUserManagementTopics(): void {
    console.log(
      '👥 Suscribiéndose a tópicos de gestión de usuarios (solo para user-list)'
    );

    // Suscribirse a los tópicos de usuarios que fueron removidos de la suscripción automática
    this.subscribeToDynamicTopic('/topic/users', 'USER_OPERATIONS');
    this.subscribeToDynamicTopic('/topic/users/created', 'USER_CREATED');
    this.subscribeToDynamicTopic('/topic/users/updated', 'USER_UPDATED');
    this.subscribeToDynamicTopic('/topic/users/deleted', 'USER_DELETED');
    this.subscribeToDynamicTopic(
      '/topic/users/deactivated',
      'USER_DEACTIVATED'
    );
  }

  /**
   * ⚠️ Suscribirse a tópicos de estado de usuarios
   * Solo para uso en páginas como user-list que necesitan monitorear estados de conexión
   */
  subscribeToUserStatusTopics(): void {
    console.log(
      '👥 Suscribiéndose a tópicos de estado de usuarios (solo para user-list)'
    );

    // Suscribirse a los tópicos de estado que fueron removidos de la suscripción automática
    this.subscribeToDynamicTopic('/topic/users/status', 'USER_STATUS');
    this.subscribeToDynamicTopic('/topic/users/status/all', 'USER_STATUS_ALL');
  }

  /**
   * Enviar mensaje al servidor
   */
  sendMessage(destination: string, body?: any): void {
    if (!this.client?.connected) {
      console.warn(
        '⚠️ WebSocket no conectado, no se puede enviar mensaje a:',
        destination
      );
      return;
    }

    try {
      if (body) {
        this.client.send(destination, {}, JSON.stringify(body));
      } else {
        this.client.send(destination, {}, '{}');
      }
    } catch (error) {
      console.error('❌ Error al enviar mensaje WebSocket:', error);
    }
  }

  /**
   * Verificar si está conectado
   */
  isConnected(): boolean {
    return this.client?.connected || false;
  }

  /**
   * 🆕 Método para forzar reconexión manual
   */
  forceReconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.hideServerDownAlert();

    setTimeout(() => {
      this.connect();
    }, 1000);
  }

  /**
   * 🆕 Método para obtener estadísticas de conexión
   */
  getConnectionStats(): any {
    return {
      isConnected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      isConnecting: this.isConnecting,
      isServerDown: this.isServerDown,
      silentMessageCount: this.silentMessageCount,
      hasToken: !!this.auth.getToken(),
    };
  }

  /**
   * 🆕 Método para debug - mostrar todos los topics suscritos
   */
  getSubscribedTopics(): string[] {
    // Este método requeriría modificar STOMP para trackear suscripciones
    // Por ahora retorna los topics conocidos
    return [
      '/topic/notifications',
      '/topic/notifications/deleted',
      '/topic/notifications/read',
      '/topic/notifications/broadcast',
      '/topic/notifications/broadcast/deleted',
      '/topic/notifications/broadcast/read',
      '/topic/notifications/admin',
      '/topic/notifications/admin/deleted',
      '/topic/notifications/admin/read',
      '/topic/faqs',
      '/topic/anuncios',
      '/topic/anuncios/recientes',
      '/topic/anuncios/new',
      '/topic/anuncios/update',
      '/topic/anuncios/delete',
      '/topic/anuncios/recientes/delete',
      '/topic/anuncios/activos',
      '/topic/anuncios/todos',
    ];
  }

  /**
   * Limpiar recursos
   */
  cleanup(): void {
    this.disconnect();
    this.hideServerDownAlert();
    this.messages$.complete();
    this.connected$.complete();
  }
}
